import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { KonamiProvider, useKonami } from "@/context/KonamiContext";
import { KonamiEffect } from "@/components/KonamiEffect";
import { VimNavProvider } from "@/context/VimNavContext";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useWebVitals } from "@/hooks/useWebVitals";
import { Toaster } from "@/components/ui/sonner";
import { Analytics } from "@vercel/analytics/react";

const MainLayout = lazy(() => import("@/components/layouts/MainLayout"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const ProjectsPage = lazy(() => import("@/pages/ProjectsPage"));
const StackPage = lazy(() => import("@/pages/StackPage"));
const LogsPage = lazy(() => import("@/pages/LogsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const CardPage = lazy(() => import("@/pages/CardPage"));

const AppInner = () => {
  const { konamiMode, setKonamiModeDirect } = useKonami();

  // Monitor Web Vitals for performance tracking
  useWebVitals({
    enabled: true,
    debug: process.env.NODE_ENV === "development",
    reportToAnalytics: true,
  });

  return (
    <BrowserRouter>
      <VimNavProvider
        konamiMode={konamiMode}
        onToggleKonami={active => setKonamiModeDirect(active)}
      >
        <ErrorBoundary>
          <Suspense fallback={<LoadingScreen konamiMode={konamiMode} />}>
            <Routes>
              <Route path="/" element={<MainLayout />}>
                <Route index element={<HomePage />} />
                <Route path="about-me" element={<AboutPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="stack" element={<StackPage />} />
                <Route path="logs" element={<LogsPage />} />
                <Route path="digital-card" element={<CardPage />} />
              </Route>
              <Route path="404.html" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster />
            {!window.location.hostname.includes("github.io") && <Analytics />}
          </Suspense>
        </ErrorBoundary>
      </VimNavProvider>
    </BrowserRouter>
  );
};

const App = () => (
  <KonamiProvider>
    <KonamiEffect />
    <AppInner />
  </KonamiProvider>
);

export default App;
